<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import {
  api,
  type DailyRiskMetricRow,
  type RiskBaseline,
  type RiskThreshold,
} from '../api/client'
import { navRiskFromPoints, streamCycleWinRate, thresholdScopes, thresholdsForScope, sharpeGradedFrom, type NavRisk } from './riskPanelHelpers'
import { useFundEnablement } from '../composables/useFundEnablement'
import { useAuth } from '../composables/useAuth'
import { usePortfolio } from '../composables/usePortfolio'
import RiskTile from './RiskTile.vue'
import RiskLineChart, { type RiskChartSeries, type RiskChartBaseline } from './RiskLineChart.vue'
import { metricSeries, drawdownSeries, winRateSeries } from './riskChartSeries'
import SignalHealthBlock from './SignalHealthBlock.vue'
import PotParityView from './PotParityView.vue'
import RiskRulesView from './RiskRulesView.vue'
import { GATED_FUNDS } from './nav/fundConfig'

// Linked users Task 9 fix round 1 (coordinator M4): a viewer never sees the
// "Save thresholds" button — the server enforces read-only anyway (Task 4);
// this only avoids a dead control.
const { isViewer } = useAuth()

// 2026-08-05 risk-screen-per-fund, Task 7: reads across ALL fund_ids in one
// call now (no active_fund query filter — see /api/risk/metrics). Per-fund
// tile visibility comes from per-user fund access (useFundEnablement.hasAccess,
// spec 2026-09-30), not the active dashboard hostname/portfolio.
const { hasAccess, isFundVisible, canView } = useFundEnablement()
const { portfolio } = usePortfolio()

// Signal-health tiles (spec 2026-10-23): live funds the user may access + gated funds they may view.
const signalHealthFunds = computed<string[]>(() => [
  ...(['BGA', 'JH', 'GOLD', 'GGE'] as const).filter(f => hasAccess(f)),
  ...GATED_FUNDS.filter(f => canView(f)),
])

// ── Metrics | Live vs backtest | Signal health | Rules switch (remembered per viewer; localStorage is optional) ──
// Signal health became its own view 2026-10-28 (Paolo); it used to sit inside Metrics. Rules: pot de-risking rules, 2026-11-01.
type RiskView = 'metrics' | 'parity' | 'health' | 'rules'
const RISK_VIEW_KEY = 'sw.risk.view'
function readRiskView(): RiskView {
  try {
    const v = localStorage.getItem(RISK_VIEW_KEY)
    return v === 'parity' || v === 'health' || v === 'rules' ? v : 'metrics'
  } catch { return 'metrics' }
}
const riskView = ref<RiskView>(readRiskView())
function setRiskView(v: RiskView) {
  riskView.value = v
  try { localStorage.setItem(RISK_VIEW_KEY, v) } catch { /* storage unavailable: choice just isn't remembered */ }
}

// ── State ──
const metrics = ref<DailyRiskMetricRow[]>([])
const baselines = ref<RiskBaseline[]>([])
const thresholds = ref<RiskThreshold[]>([])
const loading = ref(true)
const RISK_HISTORY_DAYS = 2000
let timer: ReturnType<typeof setInterval> | null = null

// ── Collapsible sections ──
const showStreamBreakdown = ref(false)
const showThresholdConfig = ref(false)

// ── Editable thresholds ──
const editedThresholds = ref<RiskThreshold[]>([])

// V100 per-fund scoping: one tab per threshold scope. BGA + COMBINED are
// always shown; JH/GOLD follow the user's fund flags and GGE appears once
// the fund has produced metric rows — the same gating as the tiles above.
const selectedScope = ref('BGA')
const visibleScopes = computed(() =>
  thresholdScopes(editedThresholds.value).filter(s => {
    if (s === 'BGA') return hasAccess('BGA')
    if (s === 'JH') return hasAccess('JH')
    if (s === 'GOLD') return hasAccess('GOLD')
    if (s === 'GGE') return latestGge.value !== null && hasAccess('GGE')
    // 2026-10-11: gated scopes only for a caller who can view the fund (the API already omits them
    // otherwise — this is the client-side belt, fail-closed while /me loads).
    if (s === 'JAPAN' || s === 'CHARTERIS') return canView(s)
    return true
  })
)
// thresholdsForScope returns the same row objects, so v-model edits below
// mutate editedThresholds directly and Save posts every scope's rows.
const scopedEditedThresholds = computed(() =>
  thresholdsForScope(editedThresholds.value, selectedScope.value)
)
const saveStatus = ref<'idle' | 'saving' | 'saved' | 'error'>('idle')
const saveError = ref('')

// ── Derived data ──
const combinedRows = computed(() =>
  metrics.value
    .filter(r => r.stream === 'combined')
    .sort((a, b) => a.date.localeCompare(b.date))
)

const bgaRows = computed(() =>
  metrics.value
    .filter(r => r.stream === 'bga')
    .sort((a, b) => a.date.localeCompare(b.date))
)

const jhRows = computed(() =>
  metrics.value
    .filter(r => r.stream === 'jh')
    .sort((a, b) => a.date.localeCompare(b.date))
)

const goldRows = computed(() =>
  metrics.value
    .filter(r => r.stream === 'gold')
    .sort((a, b) => a.date.localeCompare(b.date))
)

const ggeRows = computed(() =>
  metrics.value
    .filter(r => r.stream === 'gge')
    .sort((a, b) => a.date.localeCompare(b.date))
)

const latestCombined = computed(() =>
  combinedRows.value.length > 0 ? combinedRows.value[combinedRows.value.length - 1] : null
)

const latestBga = computed(() =>
  bgaRows.value.length > 0 ? bgaRows.value[bgaRows.value.length - 1] : null
)

const latestJh = computed(() =>
  jhRows.value.length > 0 ? jhRows.value[jhRows.value.length - 1] : null
)

const latestGold = computed(() =>
  goldRows.value.length > 0 ? goldRows.value[goldRows.value.length - 1] : null
)

// GGE has no per-user enabled flag on /api/auth/me (arming is via the
// allocation row, GOLD-style) — the tile/row appear once the reconciler
// has produced any gge stream rows, i.e. once the user actually trades it.
const latestGge = computed(() =>
  ggeRows.value.length > 0 ? ggeRows.value[ggeRows.value.length - 1] : null
)

// Plan 2026-09-29 Task 15: the gated fund's own stream. The server writes japan rows only for
// users entitled to JAPAN (JobWorkerService) and /api/risk/metrics drops gated streams the caller
// cannot view; the panel filters again through the view predicate (canView = entitled, or a
// superuser's read-only preview; fail-closed while /me loads), so a non-viewer never renders one.
const japanRows = computed(() =>
  canView('JAPAN')
    ? metrics.value.filter(r => r.stream === 'japan').sort((a, b) => a.date.localeCompare(b.date))
    : [])
const latestJapan = computed(() =>
  japanRows.value.length > 0 ? japanRows.value[japanRows.value.length - 1] : null)
const showJapanEmptyNote = computed(() => portfolio.value === 'japan' && latestJapan.value === null)

// Charteris Phase 3 (spec 2026-10-05 §7): the same treatment for CHARTERIS — the server's gated-fund
// loop (JobWorkerService, keyed off entitlements) writes a 'charteris' stream only for an entitled
// user with real activity; the panel re-checks isFundVisible (fail-closed while /me loads).
const charterisRows = computed(() =>
  canView('CHARTERIS')
    ? metrics.value.filter(r => r.stream === 'charteris').sort((a, b) => a.date.localeCompare(b.date))
    : [])
const latestCharteris = computed(() =>
  charterisRows.value.length > 0 ? charterisRows.value[charterisRows.value.length - 1] : null)
const showCharterisEmptyNote = computed(() => portfolio.value === 'silver' && latestCharteris.value === null)

// 2026-10-13 (Paolo: "amati does not yet appear in the risk pane"): AMATI, gated exactly like CHARTERIS.
// Nobody holds it before Phase 3, so a viewer sees the no-position tile with its NAV risk; a row (once the
// server writes an 'amati' stream) shows the real figures with no further change here.
const latestAmati = computed(() => {
  if (!canView('AMATI')) return null
  const rows = metrics.value.filter(r => r.stream === 'amati').sort((a, b) => a.date.localeCompare(b.date))
  return rows.length > 0 ? rows[rows.length - 1] : null
})

// MINERS (Phase 1C 2026-10-25): gated exactly like AMATI; no 'miners' stream exists until Phase 3, so a viewer
// gets the no-position tile with its NAV risk.
const latestMiners = computed(() => {
  if (!canView('MINERS')) return null
  const rows = metrics.value.filter(r => r.stream === 'miners').sort((a, b) => a.date.localeCompare(b.date))
  return rows.length > 0 ? rows[rows.length - 1] : null
})

// JAPANESG (2026-10-28): gated exactly like MINERS; no 'japanesg' stream exists until it joins the pot, so a viewer
// gets the no-position tile with its NAV risk.
const latestJapanEsg = computed(() => {
  if (!canView('JAPANESG')) return null
  const rows = metrics.value.filter(r => r.stream === 'japanesg').sort((a, b) => a.date.localeCompare(b.date))
  return rows.length > 0 ? rows[rows.length - 1] : null
})

// ELECTRUM (2026-10-31): gated exactly like TECH; no 'electrum' stream exists until it joins the pot, so a viewer
// gets the no-position tile with its NAV risk.
const latestElectrum = computed(() => {
  if (!canView('ELECTRUM')) return null
  const rows = metrics.value.filter(r => r.stream === 'electrum').sort((a, b) => a.date.localeCompare(b.date))
  return rows.length > 0 ? rows[rows.length - 1] : null
})

// TECH (2026-10-28): gated exactly like JAPANESG; no 'tech' stream exists until it joins the pot, so a viewer
// gets the no-position tile with its NAV risk.
const latestTech = computed(() => {
  if (!canView('TECH')) return null
  const rows = metrics.value.filter(r => r.stream === 'tech').sort((a, b) => a.date.localeCompare(b.date))
  return rows.length > 0 ? rows[rows.length - 1] : null
})

// 2026-10-11 (Paolo: "Charteris does not even appear on the risk pane"): a gated fund's tile is
// shown whenever the caller can VIEW the fund — not only once a row exists — so a superuser's
// pre-grant preview (and an entitled user before the first P&L day) gets an explicit "no position
// yet" tile with the fund's own NAV risk (official NAV, buy & hold, last 6 months) instead of
// silently nothing. With a row, the real figures as before.
const GATED_RISK_FUNDS = [
  { fund: 'JAPAN', stream: 'japan', title: 'Japan' },
  { fund: 'CHARTERIS', stream: 'charteris', title: 'Charteris' },
  { fund: 'AMATI', stream: 'amati', title: 'Amati' },
  { fund: 'MINERS', stream: 'miners', title: 'Miners' },
  { fund: 'JAPANESG', stream: 'japanesg', title: 'Japan ESG' },
  { fund: 'TECH', stream: 'tech', title: 'Tech' },
  { fund: 'ELECTRUM', stream: 'electrum', title: 'Electrum' },
  { fund: 'JHTECH', stream: 'jhtech', title: 'JHTech' },
  { fund: 'ASIATECH', stream: 'asiatech', title: 'AsiaTech' },
  { fund: 'BARINGSOIL', stream: 'baringsoil', title: 'BaringsOil' },
  { fund: 'SPWORLD', stream: 'spworld', title: 'SPWorld' },
  { fund: 'SMALLCAP', stream: 'smallcap', title: 'SmallCap' },
] as const
type GatedRiskFund = typeof GATED_RISK_FUNDS[number]['fund']
const gatedNavRisk = ref<Partial<Record<GatedRiskFund, NavRisk | null>>>({})

function latestGated(fund: GatedRiskFund): DailyRiskMetricRow | null {
  return fund === 'JAPAN' ? latestJapan.value : fund === 'CHARTERIS' ? latestCharteris.value : fund === 'AMATI' ? latestAmati.value : fund === 'MINERS' ? latestMiners.value : fund === 'JAPANESG' ? latestJapanEsg.value : fund === 'TECH' ? latestTech.value : fund === 'ELECTRUM' ? latestElectrum.value
    : latestStream(fund, GATED_RISK_FUNDS.find(g => g.fund === fund)!.stream)
}

// JHTECH, ASIATECH, BARINGSOIL, SPWORLD, SMALLCAP (2026-10-31): gated like ELECTRUM, read through one helper instead of a
// computed per fund. No stream exists until a fund joins the pot, so a viewer gets the no-position tile.
const PACK_RISK_FUNDS = GATED_RISK_FUNDS.filter(g => g.fund === 'JHTECH' || g.fund === 'ASIATECH' || g.fund === 'BARINGSOIL' || g.fund === 'SPWORLD' || g.fund === 'SMALLCAP')
function latestStream(fund: GatedRiskFund, stream: string): DailyRiskMetricRow | null {
  if (!canView(fund)) return null
  const rows = metrics.value.filter(r => r.stream === stream).sort((a, b) => a.date.localeCompare(b.date))
  return rows.length > 0 ? rows[rows.length - 1] : null
}

/** Viewable gated funds with no risk row yet (once metrics have loaded) — the explicit empty state. */
const gatedPlaceholders = computed(() =>
  GATED_RISK_FUNDS
    .filter(g => !loading.value && canView(g.fund) && latestGated(g.fund) === null)
    .map(g => ({
      ...g,
      note: isFundVisible(g.fund)
        ? 'No position yet — real figures appear after the first P&L day.'
        : 'No position yet — preview (not granted). Real figures appear once granted and held.',
    })))

async function loadGatedNavRisk(fund: GatedRiskFund) {
  if (fund in gatedNavRisk.value) return
  gatedNavRisk.value = { ...gatedNavRisk.value, [fund]: null }
  try {
    const res = await api.fundNavHistory(fund, '6m')
    gatedNavRisk.value = { ...gatedNavRisk.value, [fund]: navRiskFromPoints(res.points ?? []) }
  } catch {
    // NAV risk is a nicety on the empty tile — the "no position yet" state stands without it.
  }
}

watch(() => gatedPlaceholders.value.map(g => g.fund), (funds) => {
  for (const f of funds) void loadGatedNavRisk(f)
}, { immediate: true })

// Combined trade-cycle win rate (BGA + OTP cycles) — used by the hero Win
// Rate card. Kept even though the OTP tile/row are gone: this aggregates
// cycle data ALREADY present on the `combined` row itself (otpCycle*
// fields), it does not require a separate OTP stream row or UI element.
const combinedCycleWinRate = computed(() => streamCycleWinRate(latestCombined.value).rate)
const combinedCycleCount = computed(() => streamCycleWinRate(latestCombined.value).count)

// Stream Breakdown section: combined + bga always, jh/gold per-user-flag.
// No otp row — OTP has no visible surface in RiskPanel (2026-08-05
// risk-screen-per-fund).
const breakdownRows = computed(() => {
  const rows: (DailyRiskMetricRow | null)[] = [latestCombined.value]
  if (hasAccess('BGA')) rows.push(latestBga.value)
  if (hasAccess('JH')) rows.push(latestJh.value)
  if (hasAccess('GOLD')) rows.push(latestGold.value)
  if (latestGge.value && hasAccess('GGE')) rows.push(latestGge.value)
  if (latestJapan.value) rows.push(latestJapan.value)
  if (latestCharteris.value) rows.push(latestCharteris.value)
  if (latestAmati.value) rows.push(latestAmati.value)
  if (latestMiners.value) rows.push(latestMiners.value)
  if (latestJapanEsg.value) rows.push(latestJapanEsg.value)
  if (latestTech.value) rows.push(latestTech.value)
  if (latestElectrum.value) rows.push(latestElectrum.value)
  for (const g of PACK_RISK_FUNDS) {
    const row = latestStream(g.fund, g.stream)
    if (row) rows.push(row)
  }
  return rows
})

// ── Baselines ──
// baselineFor returns the risk_baselines row value for a given (stream, metric)
// pair. Extended 2026-08-23 from the combined-only lookup so the Stream
// Breakdown table can show per-fund baseline overlays alongside per-fund
// achieved values. See PromotionBaselineDeriver — per-fund baselines are
// upserted with stream={bga,jh,gold,gge} and combined with stream=combined.
function baselineFor(stream: string, metric: string): number | null {
  const b = baselines.value.find(bl => bl.stream === stream && bl.metric === metric)
  return b ? b.value : null
}

// Legacy helper preserved for the hero cards, which are combined-only.
function baselineValue(metric: string): number | null {
  return baselineFor('combined', metric)
}

const sharpeBaseline = computed(() => baselineValue('sharpe'))
// Pot users: the fund-of-funds Sharpe baseline shows now but only grades the colour from this date (Paolo 2026-10-28).
const sharpeGradedFromDate = computed(() =>
  sharpeGradedFrom(baselines.value.find(b => b.stream === 'combined' && b.metric === 'sharpe')?.source))
const ddBaseline = computed(() => baselineValue('max_dd'))
const winRateBaseline = computed(() => baselineValue('win_rate'))

// Per-cell baseline sublabel — used in Stream Breakdown table + cards.
// Returns an empty string when there is no baseline for this stream/metric,
// so the sub-line simply doesn't render (rather than adding a distracting
// "vs —"). Formatter is passed in so the sub-line matches the cell format
// (percentage for DD/WR/CAGR, plain number for Sharpe/Sortino).
function baselineSub(stream: string, metric: string, formatter: (v: number | null) => string): string {
  const bl = baselineFor(stream, metric)
  return bl === null ? '' : `vs ${formatter(bl)}`
}

// ── Formatting ──
function fmt(v: number | null, decimals = 2): string {
  return v !== null ? v.toFixed(decimals) : '—'
}

function fmtPct(v: number | null, decimals = 1): string {
  return v !== null ? (v * 100).toFixed(decimals) + '%' : '—'
}

// See riskPanelHelpers.ts for the rationale. Extracted to a sibling
// module because <script setup> cannot contain ES exports for tests.
function fmtStreamWinRate(row: DailyRiskMetricRow | null): string {
  const { rate, count } = streamCycleWinRate(row)
  if (rate === null) return '—'
  return `${fmtPct(rate)} (${count})`
}

function classColour(cls: string): string {
  if (cls === 'green') return '#4caf50'
  if (cls === 'amber') return '#fbc02d'
  if (cls === 'red') return '#f44336'
  return '#888'
}

function classTextColour(cls: string): string {
  return cls === 'red' ? '#fff' : '#000'
}

function valueColour(v: number | null): string {
  if (v === null) return '#888'
  return v >= 0 ? '#4caf50' : '#f44336'
}

function winRateColour(v: number | null): string {
  if (v === null) return '#888'
  if (v > 0.55) return '#4caf50'
  if (v >= 0.45) return '#fbc02d'
  return '#f44336'
}

// ── Charts (lightweight-charts, 2026-10-23) ──
// Series for RiskLineChart: the whole history the API returned, the chart opens on
// the last 60 points (defaultLogicalRange) and scrolls back. Values pre-scaled to
// percent where the axis shows percent.
const sharpeSeries = computed<RiskChartSeries[] | null>(() => {
  const line120 = metricSeries(combinedRows.value, r => r.sharpe120d)
  if (line120.length < 2) return null
  return [
    { id: 'sharpe20', label: '20d', color: 'rgba(76,175,80,0.45)', data: metricSeries(combinedRows.value, r => r.sharpe20d), lineWidth: 1, dashed: true },
    { id: 'sharpe60', label: '60d', color: 'rgba(76,175,80,0.75)', data: metricSeries(combinedRows.value, r => r.sharpe60d), lineWidth: 1 },
    { id: 'sharpe120', label: '120d', color: '#4caf50', data: line120, lineWidth: 2 },
  ]
})
const sharpeBaselineLine = computed<RiskChartBaseline | null>(() =>
  sharpeBaseline.value !== null ? { value: sharpeBaseline.value, label: 'baseline', color: '#2196f3' } : null)

const ddSeries = computed<RiskChartSeries[] | null>(() => {
  const data = drawdownSeries(combinedRows.value)
  if (data.length < 2) return null
  return [{ id: 'dd', label: 'drawdown', color: '#f44336', data, lineWidth: 2, area: true }]
})
// max_dd baseline is a positive fraction; the chart plots drawdown as a negative percent.
const ddBaselineLine = computed<RiskChartBaseline | null>(() =>
  ddBaseline.value !== null ? { value: -ddBaseline.value * 100, label: 'baseline', color: '#fbc02d' } : null)

const winRateChartSeries = computed<RiskChartSeries[] | null>(() => {
  // Chart the trade-cycle win rate (matches backtest methodology) on the same three tiers
  // and line styles as the Sharpe chart (Paolo 2026-10-27).
  // The previous day-level metric has been decommissioned per project policy.
  const line120 = winRateSeries(combinedRows.value, 120)
  if (line120.length < 2) return null
  return [
    { id: 'win20', label: '20d', color: 'rgba(76,175,80,0.45)', data: winRateSeries(combinedRows.value, 20), lineWidth: 1, dashed: true },
    { id: 'win60', label: '60d', color: 'rgba(76,175,80,0.75)', data: winRateSeries(combinedRows.value, 60), lineWidth: 1 },
    { id: 'win120', label: '120d', color: '#4caf50', data: line120, lineWidth: 2 },
  ]
})
const winRateBaselineLine = computed<RiskChartBaseline | null>(() =>
  winRateBaseline.value !== null ? { value: winRateBaseline.value * 100, label: 'baseline', color: '#2196f3' } : null)


// ── Fetch ──
async function fetchData() {
  // No active_fund filter (2026-08-05 risk-screen-per-fund): one call each
  // returns rows across ALL fund_ids for this user; tiles below split by
  // row.stream.
  const results = await Promise.allSettled([
    // Full history (daily_risk_metrics starts 2026-03-31): the charts scroll back through it;
    // the tiles still read only the latest row per stream.
    api.riskMetrics(RISK_HISTORY_DAYS),
    api.riskBaselines(),
    api.riskThresholds(),
  ])
  if (results[0].status === 'fulfilled') metrics.value = results[0].value
  if (results[1].status === 'fulfilled') baselines.value = results[1].value
  if (results[2].status === 'fulfilled') {
    thresholds.value = results[2].value
    if (editedThresholds.value.length === 0) {
      editedThresholds.value = results[2].value.map(t => ({ ...t }))
    }
  }
  loading.value = false
}

// ── Save thresholds ──
async function saveThresholds() {
  for (const t of editedThresholds.value) {
    if (t.greenMaxDev < 0 || t.amberMaxDev < 0 || t.amberMaxDev <= t.greenMaxDev) {
      saveStatus.value = 'error'
      saveError.value = `${t.fund || 'BGA'} ${t.metric}: amber must exceed green, both non-negative`
      return
    }
  }
  saveStatus.value = 'saving'
  try {
    await api.riskUpdateThresholds(editedThresholds.value)
    saveStatus.value = 'saved'
    setTimeout(() => { saveStatus.value = 'idle' }, 2000)
    await fetchData()
  } catch (e: any) {
    saveStatus.value = 'error'
    saveError.value = e.message || 'Failed to save'
  }
}

onMounted(() => {
  fetchData()
  timer = setInterval(fetchData, 60_000)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<template>
  <div class="risk-panel">
    <div class="risk-view-switch" data-testid="risk-view-switch">
      <button type="button" class="rvs-btn" :class="{ active: riskView === 'metrics' }" :aria-pressed="riskView === 'metrics'"
        data-testid="risk-view-metrics" @click="setRiskView('metrics')">Metrics</button>
      <button type="button" class="rvs-btn" :class="{ active: riskView === 'parity' }" :aria-pressed="riskView === 'parity'"
        data-testid="risk-view-parity" @click="setRiskView('parity')">Live vs backtest</button>
      <button type="button" class="rvs-btn" :class="{ active: riskView === 'health' }" :aria-pressed="riskView === 'health'"
        data-testid="risk-view-health" @click="setRiskView('health')">Signal health</button>
      <button type="button" class="rvs-btn" :class="{ active: riskView === 'rules' }" :aria-pressed="riskView === 'rules'"
        data-testid="risk-view-rules" @click="setRiskView('rules')">Rules</button>
    </div>
    <PotParityView v-if="riskView === 'parity'" />
    <SignalHealthBlock v-else-if="riskView === 'health'" :visible-funds="signalHealthFunds" />
    <RiskRulesView v-else-if="riskView === 'rules'" />
    <template v-else>
    <p v-if="!loading && showJapanEmptyNote" class="japan-risk-empty" data-testid="risk-japan-empty">
      No Japan risk history yet. Japan's own row appears once a Japan holding has P&amp;L days;
      metrics are computed daily at 20:05 UTC.
    </p>
    <p v-if="!loading && showCharterisEmptyNote" class="japan-risk-empty" data-testid="risk-charteris-empty">
      No Charteris risk history yet. Charteris's own row appears once a Charteris holding has P&amp;L days;
      metrics are computed daily at 20:05 UTC.
    </p>
    <p v-if="loading" style="color: #888;">Loading risk metrics...</p>

    <template v-else-if="!latestCombined">
      <p style="color: #888;">No risk metrics available yet. Metrics are computed daily at 20:05 UTC.</p>
    </template>

    <template v-else>
      <!-- Hero Cards -->
      <div class="hero-cards">
        <div class="hero-card badge-card" :style="{ borderLeftColor: classColour(latestCombined.classification) }">
          <div class="badge" :style="{ background: classColour(latestCombined.classification), color: classTextColour(latestCombined.classification) }">
            {{ latestCombined.classification.toUpperCase() }}
          </div>
          <div class="card-label">Overall Status</div>
        </div>

        <div class="hero-card" :style="{ borderLeftColor: classColour(latestCombined.classification) }">
          <div class="card-value">{{ fmt(latestCombined.sharpe120d) }}</div>
          <div class="card-label">Sharpe 120d</div>
          <div class="card-sub" v-if="sharpeBaseline !== null">
            vs baseline {{ fmt(sharpeBaseline) }}
            <span :style="{ color: classColour(latestCombined.classification) }">
              ({{ fmtPct(latestCombined.sharpeDev120d) }})
            </span>
          </div>
          <div class="card-sub" v-if="sharpeGradedFromDate" data-testid="sharpe-graded-from"
            title="The baseline is your fund-of-funds backtest. The live 120-day window still includes days before the pot went live, so Sharpe does not set the status colour until this date.">
            not graded until {{ sharpeGradedFromDate }}
          </div>
        </div>

        <div class="hero-card" :style="{ borderLeftColor: classColour(latestCombined.classification) }">
          <div class="card-value">{{ fmtPct(latestCombined.currentDdPct) }}</div>
          <div class="card-label">Current Drawdown</div>
          <div class="card-sub" v-if="ddBaseline !== null">
            vs baseline {{ fmtPct(ddBaseline) }}
            <span :style="{ color: valueColour(latestCombined.ddDev !== null ? -latestCombined.ddDev : null) }">
              ({{ fmtPct(latestCombined.ddDev) }})
            </span>
          </div>
        </div>

        <div class="hero-card" :style="{ borderLeftColor: winRateColour(combinedCycleWinRate) }">
          <div class="card-value" :style="{ color: winRateColour(combinedCycleWinRate) }">
            {{ combinedCycleWinRate !== null ? fmtPct(combinedCycleWinRate) : '—' }}
          </div>
          <div
            class="card-label"
            title="Trade cycle = one BUY→SELL round trip (FIFO paired), across BGA + OTP. Different unit from trading days — a mostly-in-cash strategy can have few cycles but many small-positive days, so this rate is typically lower than a daily-P&L win rate. The baseline is derived from the same cycle-level metric on the currently-active promoted backtest."
          >Win Rate (120d trade cycles, {{ combinedCycleCount }} trades)</div>
          <div class="card-sub" v-if="winRateBaseline !== null">
            baseline {{ fmtPct(winRateBaseline) }}
          </div>
        </div>
      </div>

      <!-- Charts (lightweight-charts): full history, default view = last 60 points; drag to scroll, wheel to zoom, double-click to reset -->
      <div class="charts-section">
        <div class="chart-container" v-if="sharpeSeries" data-testid="risk-chart-sharpe">
          <h3 class="chart-title">Rolling Sharpe Ratio</h3>
          <RiskLineChart :series="sharpeSeries" :baseline="sharpeBaselineLine" format="number2" />
        </div>

        <div class="chart-container" v-if="ddSeries" data-testid="risk-chart-drawdown">
          <h3 class="chart-title">Portfolio Drawdown</h3>
          <RiskLineChart :series="ddSeries" :baseline="ddBaselineLine" format="pct1" />
        </div>

        <div class="chart-container" v-if="winRateChartSeries" data-testid="risk-chart-winrate">
          <h3 class="chart-title">Rolling Win Rate (trade cycles)</h3>
          <RiskLineChart :series="winRateChartSeries" :baseline="winRateBaselineLine" format="pct0" />
        </div>
      </div>

      <!-- Per-fund tiles (below the charts since 2026-11-03, Paolo): DOM order BGA | JH | Gold | Combined. Visibility
           is gated on per-user fund access (useFundEnablement.hasAccess,
           spec 2026-09-30), not the active dashboard/hostname.
           No OTP tile — OTP has no visible surface in RiskPanel.
           Japan: entitled holders only (plan 2026-09-29 Task 15); Charteris the
           same (Charteris Phase 3). -->
      <div class="tile-row">
        <RiskTile v-if="hasAccess('BGA')" data-testid="risk-tile-bga" title="BGA" :row="latestBga" />
        <RiskTile v-if="hasAccess('JH')" data-testid="risk-tile-jh" title="JH" :row="latestJh" />
        <RiskTile v-if="hasAccess('GOLD')" data-testid="risk-tile-gold" title="Gold" :row="latestGold" />
        <RiskTile v-if="latestGge && hasAccess('GGE')" data-testid="risk-tile-gge" title="GGE" :row="latestGge" />
        <template v-for="g in GATED_RISK_FUNDS" :key="g.fund">
          <RiskTile
            v-if="canView(g.fund)"
            :data-testid="'risk-tile-' + g.stream"
            :title="g.title"
            :row="latestGated(g.fund)"
            :empty-note="gatedPlaceholders.find(p => p.fund === g.fund)?.note"
            :nav-risk="gatedNavRisk[g.fund] ?? null"
          />
        </template>
        <RiskTile data-testid="risk-tile-combined" title="Combined" :row="latestCombined" />
      </div>


      <!-- Stream Breakdown -->
      <h3 class="section-toggle" @click="showStreamBreakdown = !showStreamBreakdown">
        Stream Breakdown
        <span class="toggle-icon">{{ showStreamBreakdown ? '▾' : '▸' }}</span>
      </h3>
      <div v-if="showStreamBreakdown" class="stream-section">
        <table class="desktop-table">
          <thead>
            <tr>
              <th>Stream</th><th>Sharpe</th><th>Sortino</th><th>Max DD</th>
              <th>Win Rate</th><th>PF</th><th>CAGR</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in breakdownRows" :key="row?.stream ?? 'empty' + i"
                :class="{ bold: row?.stream === 'combined' }">
              <template v-if="row">
                <td>{{ row.stream }}</td>
                <td>
                  <div>{{ fmt(row.sharpe120d) }}</div>
                  <div v-if="baselineSub(row.stream, 'sharpe', fmt)" class="cell-baseline">{{ baselineSub(row.stream, 'sharpe', fmt) }}</div>
                </td>
                <td>{{ fmt(row.sortino120d) }}</td>
                <td>
                  <div>{{ fmtPct(row.currentDdPct) }}</div>
                  <div v-if="baselineSub(row.stream, 'max_dd', fmtPct)" class="cell-baseline">{{ baselineSub(row.stream, 'max_dd', fmtPct) }}</div>
                </td>
                <td>
                  <div>{{ fmtStreamWinRate(row) }}</div>
                  <div v-if="baselineSub(row.stream, 'win_rate', fmtPct)" class="cell-baseline">{{ baselineSub(row.stream, 'win_rate', fmtPct) }}</div>
                </td>
                <td>{{ fmt(row.profitFactor120d) }}</td>
                <td>
                  <div>{{ fmtPct(row.cagr120d) }}</div>
                  <div v-if="baselineSub(row.stream, 'cagr', fmtPct)" class="cell-baseline">{{ baselineSub(row.stream, 'cagr', fmtPct) }}</div>
                </td>
                <td>
                  <span class="status-badge" :style="{ background: classColour(row.classification), color: classTextColour(row.classification) }">
                    {{ row.isWarmingUp ? `warming (${row.warmupNonzeroDays ?? 0}/20)` : row.classification }}
                  </span>
                </td>
              </template>
              <template v-else>
                <td colspan="8" style="color: #888; text-align: center;">Insufficient data</td>
              </template>
            </tr>
            <tr v-for="g in gatedPlaceholders" :key="'gated-' + g.stream" :data-testid="'risk-breakdown-' + g.stream + '-empty'">
              <td>{{ g.stream }}</td>
              <td colspan="7" style="color: #888;">{{ g.note }}</td>
            </tr>
          </tbody>
        </table>

        <div class="stream-cards">
          <div v-for="(row, i) in breakdownRows" :key="row?.stream ?? 'empty' + i"
               class="stream-card" :style="{ borderLeftColor: classColour(row?.classification ?? 'green') }">
            <template v-if="row">
              <div class="stream-card-header">
                <strong>{{ row.stream }}</strong>
                <span class="status-badge" :style="{ background: classColour(row.classification), color: classTextColour(row.classification) }">
                  {{ row.isWarmingUp ? `warming (${row.warmupNonzeroDays ?? 0}/20)` : row.classification }}
                </span>
              </div>
              <div class="stream-card-metrics">
                Sharpe: {{ fmt(row.sharpe120d) }} | Sortino: {{ fmt(row.sortino120d) }} | DD: {{ fmtPct(row.currentDdPct) }}
              </div>
              <div class="stream-card-metrics">
                Win: {{ fmtStreamWinRate(row) }} | PF: {{ fmt(row.profitFactor120d) }} | CAGR: {{ fmtPct(row.cagr120d) }}
              </div>
              <div class="stream-card-baseline" v-if="baselineFor(row.stream, 'win_rate') !== null || baselineFor(row.stream, 'sharpe') !== null">
                Baseline —
                <span v-if="baselineFor(row.stream, 'sharpe') !== null">S: {{ fmt(baselineFor(row.stream, 'sharpe')) }}</span>
                <span v-if="baselineFor(row.stream, 'max_dd') !== null"> | DD: {{ fmtPct(baselineFor(row.stream, 'max_dd')) }}</span>
                <span v-if="baselineFor(row.stream, 'win_rate') !== null"> | WR: {{ fmtPct(baselineFor(row.stream, 'win_rate')) }}</span>
                <span v-if="baselineFor(row.stream, 'cagr') !== null"> | CAGR: {{ fmtPct(baselineFor(row.stream, 'cagr')) }}</span>
              </div>
            </template>
            <template v-else>
              <div class="stream-card-metrics" style="color: #888;">Insufficient data</div>
            </template>
          </div>
          <div v-for="g in gatedPlaceholders" :key="'gated-card-' + g.stream" class="stream-card"
               :style="{ borderLeftColor: '#888' }">
            <div class="stream-card-header"><strong>{{ g.stream }}</strong></div>
            <div class="stream-card-metrics" style="color: #888;">{{ g.note }}</div>
          </div>
        </div>
      </div>

      <!-- Threshold Config -->
      <h3 class="section-toggle" @click="showThresholdConfig = !showThresholdConfig">
        Threshold Config
        <span class="toggle-icon">{{ showThresholdConfig ? '▾' : '▸' }}</span>
      </h3>
      <div v-if="showThresholdConfig" class="threshold-section">
        <div class="threshold-scope-tabs">
          <button
            v-for="s in visibleScopes"
            :key="s"
            class="scope-tab"
            :class="{ active: s === selectedScope }"
            :data-testid="'threshold-scope-' + s"
            @click="selectedScope = s"
          >{{ s === 'COMBINED' ? 'Combined' : s }}</button>
        </div>
        <div v-for="t in scopedEditedThresholds" :key="t.fund + ':' + t.metric" class="threshold-row">
          <label class="threshold-label">{{ t.metric }}</label>
          <div class="threshold-inputs">
            <div class="threshold-field">
              <span class="field-label">Green max</span>
              <input type="number" step="0.01" min="0" v-model.number="t.greenMaxDev" />
            </div>
            <div class="threshold-field">
              <span class="field-label">Amber max</span>
              <input type="number" step="0.01" min="0" v-model.number="t.amberMaxDev" />
            </div>
          </div>
        </div>
        <div class="threshold-actions">
          <button v-if="!isViewer" class="save-btn" data-testid="risk-save-thresholds-btn" @click="saveThresholds" :disabled="saveStatus === 'saving'">
            {{ saveStatus === 'saving' ? 'Saving...' : 'Save' }}
          </button>
          <span v-if="saveStatus === 'saved'" class="save-msg success">Saved</span>
          <span v-if="saveStatus === 'error'" class="save-msg error">{{ saveError }}</span>
        </div>
      </div>
    </template>
    </template>
  </div>
</template>

<style scoped>
.risk-view-switch { display: inline-flex; align-self: flex-start; max-width: 100%; border: 1px solid #333; border-radius: 6px; overflow-x: auto; }
.rvs-btn { flex: none; white-space: nowrap; min-height: 44px; padding: 0 1rem; background: #1a1a1a; color: #aaa; border: 0; cursor: pointer; font-size: 0.85rem; }
.rvs-btn.active { background: #2a2a2a; color: #eee; font-weight: 600; }
.risk-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tile-row {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

@media (min-width: 769px) {
  .tile-row { flex-direction: row; }
  /* Up to 5 tiles fit one row; with the gated funds (CHARTERIS, AMATI) a superuser sees
     8, which overflowed the page to the right (2026-10-13). Wrap into two balanced rows:
     6 → 3+3, 7-8 → 4+3 / 4+4. Counted in CSS so it follows each viewer's visible tiles. */
  .tile-row:has(> :nth-child(6)) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .tile-row:has(> :nth-child(7)) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.hero-cards {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

@media (min-width: 769px) {
  .hero-cards { flex-direction: row; }
  .hero-card { flex: 1; }
}

.hero-card {
  background: #1a1a1a;
  border: 1px solid #333;
  border-left: 3px solid #888;
  border-radius: 6px;
  padding: 0.75rem;
}

.badge-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.badge {
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  padding: 0.3rem 1rem;
  border-radius: 4px;
}

.card-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.5rem;
  font-weight: 600;
}

.card-label {
  font-size: 0.8rem;
  color: #aaa;
  margin-top: 0.25rem;
}

.card-sub {
  font-size: 0.75rem;
  color: #888;
  margin-top: 0.15rem;
}

.section-toggle {
  cursor: pointer;
  user-select: none;
  font-size: 0.9rem;
  color: #aaa;
  border-bottom: 1px solid #333;
  padding-bottom: 0.3rem;
  margin: 0;
}

.section-toggle:hover { color: #e0e0e0; }

.toggle-icon {
  font-size: 0.8rem;
  margin-left: 0.5rem;
}

.stream-section { margin-top: 0.5rem; }

.desktop-table {
  display: none;
  width: 100%;
  border-collapse: collapse;
  background: #1a1a1a;
  font-size: 0.85rem;
}

@media (min-width: 769px) {
  .desktop-table { display: table; }
  .stream-cards { display: none !important; }
}

.desktop-table th,
.desktop-table td {
  padding: 0.5rem 0.6rem;
  border-bottom: 1px solid #333;
  text-align: right;
}

.desktop-table th {
  color: #888;
  font-weight: normal;
}

.desktop-table th:first-child,
.desktop-table td:first-child { text-align: left; }

.bold td { font-weight: 600; }

.status-badge {
  font-size: 0.7rem;
  padding: 0.15rem 0.4rem;
  border-radius: 3px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stream-cards {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.stream-card {
  background: #1a1a1a;
  border: 1px solid #333;
  border-left: 3px solid #888;
  border-radius: 6px;
  padding: 0.6rem;
}

.stream-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.3rem;
}

.stream-card-metrics {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
  color: #ccc;
}

.stream-card-baseline {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.72rem;
  color: #7a7a7a;
  margin-top: 0.15rem;
}

.cell-baseline {
  font-size: 0.65rem;
  color: #7a7a7a;
  font-weight: normal;
  margin-top: 0.1rem;
  white-space: nowrap;
}

.threshold-scope-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.scope-tab {
  background: #2a2a2a;
  color: #aaa;
  border: 1px solid #3a3a3a;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
}

.scope-tab.active {
  background: #3a3a3a;
  color: #fff;
  border-color: #4caf50;
}

.threshold-section {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.threshold-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  background: #1a1a1a;
  padding: 0.5rem;
  border-radius: 4px;
}

.threshold-label {
  min-width: 100px;
  font-size: 0.85rem;
  color: #ccc;
}

.threshold-inputs {
  display: flex;
  gap: 0.75rem;
}

.threshold-field {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.field-label {
  font-size: 0.7rem;
  color: #888;
}

.threshold-section input {
  background: #0d1117;
  border: 1px solid #333;
  color: #e0e0e0;
  padding: 0.35rem 0.5rem;
  border-radius: 4px;
  width: 80px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
}

.threshold-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.25rem;
}

.save-btn {
  background: #4caf50;
  color: #000;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.save-msg { font-size: 0.8rem; }
.save-msg.success { color: #4caf50; }
.save-msg.error { color: #f44336; }

.charts-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.chart-container {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 0.5rem;
}

.chart-title {
  font-size: 0.85rem;
  color: #aaa;
  margin: 0 0 0.25rem 0.25rem;
  font-weight: normal;
}

.japan-risk-empty { color: #888; margin: 0; }
</style>
